const DEFAULT_STATUS_MESSAGE = 'Analyzing your request...';
const INTERNAL_STATUS_MARKERS = ['send_user', 'lookup_user_data', 'tool_call', 'function_call', 'backend', 'mcp'];

// Marker used to persist a service-record card inside the saved assistant message so it can be
// re-rendered on reload: [SERVICE_CARD]{...json...}[/SERVICE_CARD]. The JSON never contains the
// literal close tag, so a non-greedy match is safe even when notes include brackets.
const SERVICE_CARD_RE = /\[SERVICE_CARD\]([\s\S]*?)\[\/SERVICE_CARD\]/;

// Marker for the 3-day monitoring follow-up card CarFido posts into a driver's chat when a
// repair's outcome is still unconfirmed: [FOLLOWUP_CARD]{"ticket_id":N,"issue":"..."}[/FOLLOWUP_CARD]
const FOLLOWUP_CARD_RE = /\[FOLLOWUP_CARD\]([\s\S]*?)\[\/FOLLOWUP_CARD\]/;

// Pull the follow-up card payload out of message text. Returns the parsed card (or null) and
// the text with the marker removed.
export function extractFollowupCard(text) {
    const raw = text || '';
    const match = raw.match(FOLLOWUP_CARD_RE);
    if (!match) return { content: raw, followupCard: null };
    let followupCard = null;
    try {
        const parsed = JSON.parse(match[1]);
        if (parsed && typeof parsed === 'object') followupCard = parsed;
    } catch (e) {
        followupCard = null;
    }
    return { content: raw.replace(FOLLOWUP_CARD_RE, '').trim(), followupCard };
}

// Pull the service-card payload out of message text. Returns the parsed card (or null) and the
// text with the marker removed, so callers can display clean text and render the card separately.
export function extractServiceCard(text) {
    const raw = text || '';
    const match = raw.match(SERVICE_CARD_RE);
    if (!match) return { content: raw, serviceCard: null };
    let serviceCard = null;
    try {
        const parsed = JSON.parse(match[1]);
        if (parsed && typeof parsed === 'object') serviceCard = parsed;
    } catch (e) {
        serviceCard = null;
    }
    return { content: raw.replace(SERVICE_CARD_RE, '').trim(), serviceCard };
}

export const cleanInternalTags = (text) => {
    if (!text) return '';
    return text
        .replace(/\[GET_WORK_LIST\]/g, '')
        .replace(/\[ADD_WORK_ITEM:[\s\S]*?\]/g, '')
        .replace(/\[REMOVE_WORK_ITEM:[\s\S]*?\]/g, '')
        .replace(/\[UPDATE_WORK_ITEM:[\s\S]*?\]/g, '')
        .replace(/\[CLEAR_WORK_LIST\]/g, '')
        .replace(/\[SWITCH_VEHICLE:\s*\d+\]/g, '')
        .replace(/\[OPEN_ADD_VEHICLE\]/gi, '')
        .replace(/\[GENERATE_SUMMARY_PDF[\s\S]*?\]/g, '')
        .replace(/\[SERVICE_CARD\][\s\S]*?\[\/SERVICE_CARD\]/g, '')
        .replace(/\[FOLLOWUP_CARD\][\s\S]*?\[\/FOLLOWUP_CARD\]/g, '')
        .replace(/__PDF_URL__:[^\s]*/g, '')
        .replace(/__DOCUMENT__:[^\s]*/g, '')
        .replace(/PDF_URL:None/g, '')
        .replace(/__PDF_URL__:None/g, '')
        .trim();
};

export function extractSuggestionsAndContent(text) {
    if (!text) return { content: '', suggestions: [] };

    let content = text;
    let suggestions = [];

    const tagIndex = content.indexOf('[SUGGESTIONS:');
    if (tagIndex !== -1) {
        content = content.substring(0, tagIndex).trim();
        content = content.replace(/[\s`\]]+$/, '').trim();

        const afterTag = text.substring(tagIndex + '[SUGGESTIONS:'.length).trimStart();
        if (afterTag.startsWith('[')) {
            let depth = 0;
            let endIndex = -1;
            let inString = false;
            let escape = false;
            for (let i = 0; i < afterTag.length; i++) {
                const ch = afterTag[i];
                if (escape) {
                    escape = false;
                    continue;
                }
                if (ch === '\\') {
                    escape = true;
                    continue;
                }
                if (ch === '"') {
                    inString = !inString;
                    continue;
                }
                if (inString) continue;
                if (ch === '[') depth++;
                else if (ch === ']') {
                    depth--;
                    if (depth === 0) {
                        endIndex = i;
                        break;
                    }
                }
            }
            if (endIndex !== -1) {
                const jsonStr = afterTag.substring(0, endIndex + 1);
                try {
                    const parsed = JSON.parse(jsonStr);
                    if (Array.isArray(parsed)) {
                        suggestions = parsed.filter(s => typeof s === 'string');
                    }
                } catch (e) {
                    console.error('[CarFido] Failed to parse suggestions JSON:', e.message, '| Raw:', jsonStr);
                }
            }
        }
    }

    return { content, suggestions };
}

export const getMessageContent = (msg) => extractSuggestionsAndContent(msg?.content || '').content;

export const getMessageSuggestions = (msg) => {
    if (Array.isArray(msg?.suggestions) && msg.suggestions.length > 0) {
        return msg.suggestions.filter(s => typeof s === 'string' && s.trim());
    }
    return extractSuggestionsAndContent(msg?.content || '').suggestions;
};

export function normalizeStatusMessage(rawStatus) {
    let message = rawStatus;

    try {
        const data = JSON.parse(rawStatus);
        message = data.message || DEFAULT_STATUS_MESSAGE;
    } catch (e) {
        message = rawStatus || DEFAULT_STATUS_MESSAGE;
    }

    const normalized = String(message).trim();
    const lower = normalized.toLowerCase();

    if (
        lower.startsWith('executing') ||
        lower.startsWith('initializing dash') ||
        INTERNAL_STATUS_MARKERS.some(marker => lower.includes(marker))
    ) {
        return DEFAULT_STATUS_MESSAGE;
    }

    return normalized || DEFAULT_STATUS_MESSAGE;
}
